import { Button, Card, Input, Link, navigate, Page, Stat } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { daysLeft, fmtDateTime, fmtNum } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, must, rpc } from '../lib/supabase.ts';
import type { Notification } from '../lib/types.ts';
import { historyLabel } from './common.tsx';
import { SkeletonCards, SkeletonLines } from './loading.tsx';
import { SavedSearchList } from './Saved.tsx';

export function HomePage() {
  const { org, entitlements: e, session, can } = useOrg();
  const notes = useQuery({
    queryKey: ['notifications', 'home'],
    queryFn: () =>
      must(
        app()
          .from('notifications')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(5),
      ) as Promise<Notification[]>,
  });
  const quota = useQuery({
    queryKey: ['export_quota', org.id],
    enabled: Boolean(e?.plan),
    queryFn: () =>
      rpc<{ limit: number | null; used: number; remaining: number | null }>('export_quota', {
        p_org: org.id,
      }),
  });
  const search = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const q = String(new FormData(ev.currentTarget).get('q') ?? '').trim();
    navigate(`/app/achizitii${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  };
  const plan = e?.plan === 'trial' ? 'Perioadă de probă' : (e?.plan ?? 'fără abonament');
  return (
    <Page title={org.name}>
      <p className="-mt-2 text-sm text-gri-700">
        CUI {org.cui} · rolul tău: {org.role.name}
        {org.job_title ? ` · ${org.job_title}` : ''}
      </p>
      <Card>
        <form className="flex gap-2" onSubmit={search}>
          <Input
            name="q"
            placeholder="Caută în achiziții: seringi, hârtie A4, servicii de pază…"
            aria-label="Caută"
          />
          <Button tone="primary" type="submit">
            Caută
          </Button>
        </form>
      </Card>
      <SavedSearchList />
      {!e || quota.isLoading ? (
        <SkeletonCards count={3} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat
            label="Abonament"
            value={plan}
            sub={
              e?.plan === 'trial'
                ? `${daysLeft(e.subscription?.trial_end)} zile rămase`
                : historyLabel(e?.features.history_days)
            }
          />
          <Stat
            label="Exporturi luna aceasta"
            value={fmtNum(quota.data?.used ?? 0)}
            sub={quota.data?.limit ? `din ${fmtNum(quota.data.limit)} rânduri` : 'rânduri'}
          />
          <Stat
            label="Membri în firmă"
            value={fmtNum(e?.members)}
            sub={
              <Link to="/app/echipa" className="underline">
                Echipa
              </Link>
            }
          />
        </div>
      )}
      <Card
        title="Ultimele notificări"
        actions={
          <Link to="/app/notificari" className="text-sm underline">
            Toate
          </Link>
        }
      >
        {notes.isLoading ? (
          <SkeletonLines lines={3} />
        ) : notes.data?.length ? (
          <ul className="space-y-1 text-sm">
            {notes.data.map((n) => (
              <li key={n.id} className="flex justify-between gap-3">
                <span className={n.read_at ? '' : 'font-medium'}>{n.title}</span>
                <span className="whitespace-nowrap text-xs text-gri-700">
                  {fmtDateTime(n.created_at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gri-700">
            Nicio notificare încă.{' '}
            {can('alerts.own') ? (
              <Link to="/app/alerte" className="underline">
                Setează prima alertă
              </Link>
            ) : null}
          </p>
        )}
      </Card>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ['/app/produse', 'Produse', 'Ce s-a cumpărat, în ce cantitate și la ce preț unitar.'],
          [
            '/app/preturi',
            'Prețuri',
            'Mediana și intervalul de preț pe lună, pentru fiecare cod CPV.',
          ],
          [
            '/app/alerte',
            'Alerte',
            'Achizițiile noi care te interesează, pe email în fiecare dimineață.',
          ],
        ].map(([to, title, text]) => (
          <Link
            key={to}
            to={to as string}
            className="group flex flex-col gap-2 border-2 border-cerneala bg-hartie p-4 hover:bg-cerneala hover:text-hartie"
          >
            <div className="flex items-center justify-between text-lg font-extrabold">
              {title}
              <span aria-hidden="true" className="text-semnal">
                →
              </span>
            </div>
            <p className="text-sm text-gri-700 group-hover:text-otel">{text}</p>
          </Link>
        ))}
      </div>
    </Page>
  );
}
